// The lists screen's data and writes. Query keys start with the entity kind ('list', 'myList', 'wave'), so a change event of that
// kind refetches (lib/events.ts). A URL carries ids only: `?list=` is a shared list id (ls_) or a private one (ml_).
import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query'
import type { z } from 'zod'
import type { ListDetail, ListSummary, MyListDetail as MyListDetailSchema, MyListSummary } from '@nw/shared/api/lists'
import { api, apiPath } from '../../lib/api.ts'
import type { NameChoice } from '../touches/names.ts'

export type MyListDetail = z.infer<typeof MyListDetailSchema>
export type SharedSummary = z.infer<typeof ListSummary>
export type MySummary = z.infer<typeof MyListSummary>
export type { ListDetail }

const ID = '[0-9a-hjkmnp-tv-z]{26}'
const SHARED_ID = new RegExp(`^ls_${ID}$`)
const MINE_ID = new RegExp(`^ml_${ID}$`)

export const isSharedId = (id: string): boolean => SHARED_ID.test(id)
export const isMineId = (id: string): boolean => MINE_ID.test(id)

/** Router validateSearch: the selected list, if it is a list id (ids only in a URL). */
export function parseSearch(search: Record<string, unknown>): { list?: string } {
  return typeof search.list === 'string' && (isSharedId(search.list) || isMineId(search.list)) ? { list: search.list } : {}
}

interface Page<T> {
  items: T[]
  next: string | null
}

const pages = <T>(key: readonly string[], path: `/api/${string}`) =>
  infiniteQueryOptions({
    queryKey: [...key, 'page'] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }) => api.get<Page<T>>(apiPath(path, {}, { after: pageParam, limit: 100 }), signal),
    getNextPageParam: (last) => last.next ?? undefined,
  })

export const sharedListsQuery = pages<SharedSummary>(['list'], '/api/lists')
export const myListsQuery = pages<MySummary>(['myList'], '/api/my/lists')

export const sharedListQuery = (id: string) =>
  queryOptions({ queryKey: ['list', id] as const, queryFn: ({ signal }) => api.get<ListDetail>(apiPath('/api/lists/:listId', { listId: id }), signal) })
export const myListQuery = (id: string) =>
  queryOptions({ queryKey: ['myList', id] as const, queryFn: ({ signal }) => api.get<MyListDetail>(apiPath('/api/my/lists/:myListId', { myListId: id }), signal) })

export type Family = 'tusen' | 'betong' | 'sauda' | 'tommy'
export const FAMILIES: readonly Family[] = ['tusen', 'betong', 'sauda', 'tommy']

/** A shared list's item, or a private one: the body of list.patch and myList.patch. */
export interface SharedItem {
  orgnr: string
  roleCode: string | null
}
export type MyItem = { kind: 'org'; orgnr: string } | { kind: 'person'; personId: string } | { kind: 'contact'; contactId: string }

/** `names` answers a 409 names_in_text (the write-time name check, CONTRACTS.md section 4): keep or redact the names. */
export const createShared = (body: { name: string; origin: 'manual'; family?: Family | null; names?: NameChoice }) => api.post<ListDetail>('/api/lists', body)
export const createMine = (body: { name: string; origin: 'manual'; family?: Family | null }) => api.post<MyListDetail>('/api/my/lists', body)

export const patchShared = (id: string, body: { version: number; name?: string; family?: Family | null; add?: SharedItem[]; remove?: SharedItem[]; names?: NameChoice }) =>
  api.patch<ListDetail>(apiPath('/api/lists/:listId', { listId: id }), body)
export const patchMine = (id: string, body: { version: number; name?: string; family?: Family | null; add?: MyItem[]; remove?: MyItem[] }) =>
  api.patch<MyListDetail>(apiPath('/api/my/lists/:myListId', { myListId: id }), body)

export const deleteShared = (id: string) => api.delete<{ ok: true }>(apiPath('/api/lists/:listId', { listId: id }))
export const deleteMine = (id: string) => api.delete<{ ok: true }>(apiPath('/api/my/lists/:myListId', { myListId: id }))

export const sharePreview = (id: string) =>
  api.post<{ entries: SharedItem[]; stripped: number }>(apiPath('/api/my/lists/:myListId/share/preview', { myListId: id }), {})
export const shareList = (id: string, name: string, names?: NameChoice) =>
  api.post<ListDetail>(apiPath('/api/my/lists/:myListId/share', { myListId: id }), names ? { name, names } : { name })
